import React, { useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, Group, NavLink, Stack, Text } from "@mantine/core";
import {
    IconActivity,
    IconAdjustments,
    IconApi,
    IconBrain,
    IconBuilding,
    IconCategory,
    IconCloud,
    IconFolder,
    IconHistory,
    IconKey,
    IconPuzzle,
    IconLayoutDashboard,
    IconShieldLock,
    IconUsers,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { AdminPermissions } from "@config/permissions";
import type { AdminPermission } from "@config/permissions";
import { spacePx } from "@xplatform/design-system";

interface NavItem {
    labelKey: string;
    path: string;
    icon: React.ReactNode;
    permissions: AdminPermission[];
    /** Also required, all of them (on top of any of `permissions`). */
    allOf?: AdminPermission[];
}

interface NavSection {
    titleKey: string;
    items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
    {
        titleKey: "admin.nav.sections.insights",
        items: [
            {
                labelKey: "admin.nav.dashboard",
                path: "/admin/dashboard",
                icon: <IconLayoutDashboard size={18} />,
                permissions: [AdminPermissions.DASHBOARD_VIEW],
            },
            {
                labelKey: "admin.nav.monitoring",
                path: "/admin/monitoring",
                icon: <IconActivity size={18} />,
                permissions: [AdminPermissions.MONITORING_VIEW],
            },
            {
                labelKey: "admin.nav.organisations",
                path: "/admin/organisations",
                icon: <IconBuilding size={18} />,
                // Lists every organisation, so callers who may see all of them only;
                // the dashboard must be readable too (the list is the dashboard breakdown).
                permissions: [AdminPermissions.MONITORING_VIEW_ALL],
                allOf: [AdminPermissions.DASHBOARD_VIEW],
            },
        ],
    },
    {
        titleKey: "admin.nav.sections.configuration",
        items: [
            {
                labelKey: "admin.nav.providers",
                path: "/admin/providers",
                icon: <IconCloud size={18} />,
                permissions: [AdminPermissions.PROVIDER_VIEW],
            },
            {
                labelKey: "admin.nav.models",
                path: "/admin/models",
                icon: <IconBrain size={18} />,
                permissions: [AdminPermissions.PROVIDER_VIEW],
            },
        ],
    },
    {
        // Categories and document types (with their flows) belong to the
        // extraction module, as their admin.extraction.* permissions say.
        titleKey: "admin.nav.sections.extraction",
        items: [
            {
                labelKey: "admin.nav.categories",
                path: "/admin/categories",
                icon: <IconCategory size={18} />,
                permissions: [AdminPermissions.CATEGORY_VIEW, AdminPermissions.CATEGORY_MANAGE],
            },
            {
                labelKey: "admin.nav.documentTypes",
                path: "/admin/doctypes",
                icon: <IconFolder size={18} />,
                permissions: [AdminPermissions.DOCTYPE_VIEW, AdminPermissions.DOCTYPE_MANAGE],
            },
        ],
    },
    {
        titleKey: "admin.nav.sections.mapping",
        items: [
            {
                labelKey: "admin.nav.mappingConfigs",
                path: "/admin/mapping-configs",
                icon: <IconAdjustments size={18} />,
                permissions: [AdminPermissions.MAPPING_CONFIG_VIEW],
            },
        ],
    },
    {
        // BeeHive users, roles and permissions of this environment's application.
        // Core requires `admin.access` on top of the view permission.
        titleKey: "admin.nav.sections.access",
        items: [
            {
                labelKey: "admin.nav.accessUsers",
                path: "/admin/access/users",
                icon: <IconUsers size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
            {
                labelKey: "admin.nav.accessRoles",
                path: "/admin/access/roles",
                icon: <IconShieldLock size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
            {
                // A new service starts here: its module, then its permissions and credentials.
                labelKey: "admin.nav.accessModules",
                path: "/admin/access/modules",
                icon: <IconPuzzle size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
            {
                labelKey: "admin.nav.accessPermissions",
                path: "/admin/access/permissions",
                icon: <IconKey size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
            {
                labelKey: "admin.nav.accessApiKeys",
                path: "/admin/access/api-keys",
                icon: <IconApi size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
            {
                labelKey: "admin.nav.accessAudit",
                path: "/admin/access/audit",
                icon: <IconHistory size={18} />,
                permissions: [AdminPermissions.ACCESS_VIEW],
                allOf: [AdminPermissions.ACCESS],
            },
        ],
    },
];

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { hasPermission } = useAdminPermissions();

    const sections = useMemo(
        () =>
            NAV_SECTIONS.map((section) => ({
                ...section,
                items: section.items.filter((item) =>
                    item.permissions.some(hasPermission) && (item.allOf ?? []).every(hasPermission)),
            })).filter((section) => section.items.length > 0),
        [hasPermission],
    );

    return (
        <Group align="stretch" gap={0} wrap="nowrap" mih="100%">
            <Box
                component="nav"
                aria-label={t("admin.nav.label")}
                w={232}
                miw={232}
                py="md"
                px="xs"
                bg="var(--mantine-color-body)"
                style={{ borderRight: "1px solid var(--mantine-color-default-border)" }}
            >
                <Stack gap="md">
                    {sections.map((section) => (
                        <Stack key={section.titleKey} gap={spacePx[1]}>
                            <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm">
                                {t(section.titleKey)}
                            </Text>
                            {section.items.map((item) => (
                                <NavLink
                                    key={item.path}
                                    label={t(item.labelKey)}
                                    leftSection={item.icon}
                                    active={location.pathname.startsWith(item.path)}
                                    onClick={() => navigate(item.path)}
                                />
                            ))}
                        </Stack>
                    ))}
                </Stack>
            </Box>
            {/* The one place page padding is set: 16px (the `md` spacing token) on every page. */}
            <Box component="main" p="md" flex={1} miw={0}>
                <Outlet />
            </Box>
        </Group>
    );
};

export default AdminPage;
